import { ArrowLeft, ArrowUpRight, Check, CircleDot, Wrench } from "lucide-react";
import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ProjectVisual } from "../components/ProjectVisual";
import { SystemDiagram } from "../components/SystemDiagram";
import { projects } from "../data/projects";

export function ProjectDetailPage() {
  const { slug } = useParams();
  const project = projects.find((item) => item.slug === slug);

  useEffect(() => {
    document.title = project ? `${project.title} — Yuddhish Chooah` : "Project not found — Yuddhish Chooah";
    return () => { document.title = "Yuddhish Chooah — AI Engineer & Robotics Researcher"; };
  }, [project]);

  if (!project?.detail) {
    return (
      <main id="main-content" className="not-found shell">
        <p className="eyebrow">404 / project note</p><h1>This case study is not published yet.</h1>
        <p>The project may be part of an ongoing collection or capability summary.</p>
        <Link className="button button-primary" to="/projects"><ArrowLeft aria-hidden="true" /> Back to projects</Link>
      </main>
    );
  }

  const detail = project.detail;
  return (
    <main id="main-content" className="case-study">
      <div className="case-study-hero shell">
        <Link className="back-link" to="/projects"><ArrowLeft aria-hidden="true" /> All projects</Link>
        <div className="case-hero-grid">
          <div>
            <div className="project-card-topline"><span className="project-index">CASE / {String(projects.indexOf(project) + 1).padStart(2, "0")}</span><span className={`status-label status-${project.status.toLowerCase()}`}>{project.status}</span></div>
            <p className="eyebrow">{project.eyebrow}</p>
            <h1>{project.title}</h1>
            <p className="case-summary">{project.summary}</p>
            <ul className="tag-list">{project.categories.map((category) => <li key={category}>{category}</li>)}</ul>
            <div className="project-links">
              {project.links.filter((link) => link.external && !link.placeholder).map((link) => <a key={link.label} href={link.href} target="_blank" rel="noreferrer">{link.label}<ArrowUpRight aria-hidden="true" /></a>)}
            </div>
          </div>
          <div className="case-visual panel"><ProjectVisual type={project.visual} /></div>
        </div>
      </div>

      <div className="case-study-body shell">
        <section className="case-context">
          <div><p className="eyebrow">01 / Context</p><h2>The engineering question</h2></div>
          <div><p>{detail.context}</p><blockquote>{project.problem}</blockquote></div>
        </section>

        <section className="case-split">
          <div className="case-panel panel"><p className="eyebrow">02 / Objectives</p><h2>What the system needed to do</h2><ul className="check-list">{detail.objectives.map((item) => <li key={item}><Check aria-hidden="true" />{item}</li>)}</ul></div>
          <div className="case-panel panel"><p className="eyebrow">03 / Approach</p><h2>How the pieces fit</h2><ol className="numbered-list">{detail.approach.map((item, index) => <li key={item}><span>{String(index + 1).padStart(2, "0")}</span>{item}</li>)}</ol></div>
        </section>

        {project.visual === "lidar" && <section className="case-architecture"><div className="case-section-heading"><p className="eyebrow">04 / Architecture</p><h2>From sensor signal to motion.</h2></div><SystemDiagram /></section>}

        {detail.hardware && <section className="hardware-strip"><div><Wrench aria-hidden="true" /><p className="eyebrow">Hardware stack</p></div><ul>{detail.hardware.map((item) => <li key={item}>{item}</li>)}</ul></section>}

        <section className="case-results">
          <div className="case-section-heading"><p className="eyebrow">05 / Testing & results</p><h2>Reported under tested conditions.</h2><p>{detail.testing}</p></div>
          <div className="result-grid">{detail.results.map((result) => <article className="result-card panel" key={result.label}><strong>{result.value}</strong><h3>{result.label}</h3><p>{result.note}</p></article>)}</div>
        </section>

        <section className="case-split">
          <div className="case-panel panel"><p className="eyebrow">06 / Challenges</p><h2>Where the system resisted</h2><ul className="dot-list">{detail.challenges.map((item) => <li key={item}><CircleDot aria-hidden="true" />{item}</li>)}</ul></div>
          <div className="case-panel panel case-panel-accent"><p className="eyebrow">07 / Lessons</p><h2>What carries forward</h2><ul className="check-list">{detail.lessons.map((item) => <li key={item}><Check aria-hidden="true" />{item}</li>)}</ul></div>
        </section>

        <nav className="case-next" aria-label="Case study navigation">
          <p>Next system</p>
          <Link to={project.slug === "lidar-navigation" ? "/projects/robotic-vacuum" : "/projects/lidar-navigation"}>{project.slug === "lidar-navigation" ? projects[1].title : projects[0].title}<ArrowUpRight aria-hidden="true" /></Link>
        </nav>
      </div>
    </main>
  );
}
